import { customElement, property } from 'lit/decorators.js';
import { html } from 'lit';
import { TmElement } from '@thiememeulenhoff/lit-sdk';
import { provide } from '@lit/context';
import {
  LoadingContext,
  loadingStateContext,
} from '../../contexts/loading.context';
import { tagName } from '../../helpers/element.helper';

const tag = tagName('load-state');

@customElement(tag)
export class LoadStateComponent extends TmElement {
  private loadingElements = new Set<string>();

  private debounceTimer: ReturnType<typeof setTimeout> | null = null;

  @property({ type: Boolean, reflect: true })
  public elementsLoadingComplete = false;

  @property({ type: Boolean })
  public expectLoadingElements = true;

  @provide({ context: loadingStateContext })
  imageLoadingProvider: LoadingContext = {
    registerElement: (id: string) => {
      this.loadingElements.add(id);
      this.#checkLoadingState();
    },
    markElementLoaded: (id: string) => {
      this.loadingElements.delete(id);
      this.#checkLoadingState();
    },
    unregisterElement: (id: string) => {
      this.loadingElements.delete(id);
      this.#checkLoadingState();
    },
  };

  override updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);

    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      // If there are no expected loading items, do the check after a short delay (to give elements a chance to still register)
      // This should prevent the loading state to get stuck if there are no loading items at all (which is almost never the case)
      if (!this.expectLoadingElements) {
        this.#checkLoadingState();
      }
    }, 1_000);
  }

  async #checkLoadingState() {
    await this.updateComplete;

    this.elementsLoadingComplete = this.loadingElements.size === 0;
  }

  override render() {
    return html`<slot></slot>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'a-load-state': LoadStateComponent;
  }
}
